import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Loader2, AlertTriangle } from "lucide-react";

const MAX_ATTEMPTS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useCart();
  const [state, setState] = useState("checking"); // checking | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          navigate(`/order-confirmation/${data.order_number}`, { replace: true });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate, refresh]);

  return (
    <div className="max-w-xl mx-auto px-4 py-28 text-center" data-testid="payment-success-page">
      {state === "checking" ? (
        <>
          <Loader2 className="w-14 h-14 text-gold mx-auto animate-spin" strokeWidth={1.4} />
          <h1 className="font-serif text-4xl mt-8 text-charcoal">Confirming your payment…</h1>
          <p className="text-gray-500 mt-3">Please wait a moment — this can take a few seconds. Do not close this window.</p>
        </>
      ) : (
        <>
          <AlertTriangle className="w-14 h-14 text-pink mx-auto" strokeWidth={1.4} />
          <h1 className="font-serif text-4xl mt-8 text-charcoal">We couldn't confirm your payment</h1>
          <p className="text-gray-500 mt-3">
            If you completed payment, your order will be confirmed shortly and you'll receive an email.
            Otherwise, please try again from your bag.
          </p>
          <button onClick={() => navigate("/cart")} data-testid="payment-back-to-cart"
            className="inline-block mt-8 bg-pink text-white px-9 py-4 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">
            Back to Bag
          </button>
        </>
      )}
    </div>
  );
}
